import axios from 'axios'
import { Configurations } from 'config/config'

/**
 * Axios instance to get the data from local json file.
 * @type {AxiosInstance}
 */
axios.create({
    baseURL: Configurations.azureADRedirectURL,
})

/**
 * Asynchronous function to fetch claim data.
 *
 * @param {Object} request - The request object with claim number.
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const fetchClaim = async (claimNumber) => {
    // Use axios to send a POST request to the specified endpoint
    const claimDataResponse = axios
        .get(
            `${Configurations.apiEndPoints.baseUrl}api/claim/V2/${claimNumber}`
        )
        .then((response) => {
            // If the request is successful, return the response
            return response
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('fetchClaimData::', error)
            return null
        })
    return claimDataResponse
}

/**
 * Asynchronous function to fetch templates data.
 *
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const fetchTemplates = async (request) => {
    // Use axios to send a GET request to the specified endpoint
    const templatesResponse = axios
        .get(
            Configurations.apiEndPoints.mergeDoc +
                'templates/email/' +
                request.user
        )
        .then((response) => {
            // If the request is successful, return the response
            return response
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.log('fetchTemplatesData::', error)
            return null
        })
    return templatesResponse
}

/**
 * Asynchronous function to fetch current documents data.
 *
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const fetchDocumentsByEmail = async (request) => {
    // Use axios to send a GET request to the specified endpoint
    const currentDocsResponse = axios
        .get(
            Configurations.apiEndPoints.mergeDoc +
                'documents/email/' +
                request.user
        )
        .then((response) => {
            // If the request is successful, return the response
            return response
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.log('fetchDocumentsByEmail::', error)
            return null
        })
    return currentDocsResponse
}

/**
 * Asynchronous function to fetch SPARs data.
 *
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const fetchSPARs = async (username) => {
    // Use axios to send a GET request to the specified endpoint
    const sPARSResponse = axios
        .get(`${Configurations.apiEndPoints.mergeDoc}spars/email/${username}`)
        .then((response) => {
            // If the request is successful, return the response
            return response
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.log('fetchSPARs::', error)
            return null
        })
    return sPARSResponse
}

/**
 * Asynchronous function to fetch SPAR data.
 *
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const getSparByID = async (sparId) => {
    // Use axios to send a GET request to the specified endpoint
    const sPARSResponse = axios
        .get(Configurations.apiEndPoints.mergeDoc + 'spars/' + sparId)
        .then((response) => {
            // If the request is successful, return the response
            return response
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.log('getSparByID::', error)
            return null
        })
    return sPARSResponse
}

/**
 * Asynchronous function to create a claim document.
 *
 * @param {Object} claimData  - Claim data object with claim number, claimant, employer, and insurer.
 * @param {String} templateString - HTML template string after claim data is merged, and ready to be created as a document.
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const createClaimDocument = async (
    claimNumber,
    templateString,
    templateControls,
    templateId,
    userName
) => {
    // Use axios to send a POST request to the specified endpoint
    let request = {
        claimNo: claimNumber,
        isCommitted: 0,
        isReadyToCommit: 0,
        templateId: templateId,
        documentContent: templateString,
        documentControls: templateControls ? templateControls : '{}',
        actionBy: userName,
    }

    const createClaimDocumentResponse = axios
        .post(Configurations.apiEndPoints.mergeDoc + 'documents', request)
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.log('createClaimDocument::', error)
            return null
        })
    return createClaimDocumentResponse
}

/**
 * Asynchronous function to update documentHtml by calling an API.
 *
 * @param {Object} requestData - The request data object.
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const updateApiDocument = async (
    documentId,
    templateId,
    claimData,
    template,
    templateControls,
    userName,
    isCommited,
    isReadyToCommit,
    primaryClaimNumber
) => {
    // Use axios to send a PUT request to the specified endpoint
    let request = {
        id: documentId,
        claimNo: primaryClaimNumber,
        isCommitted: isCommited,
        isReadyToCommit: isReadyToCommit,
        documentContent: template,
        documentControls: templateControls ? templateControls : '{}',
        templateId: templateId,
        actionBy: userName,
    }
    const updateDocumentHtmlResponse = axios
        .put(Configurations.apiEndPoints.mergeDoc + 'documents', request)
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.log('updateDocumentHtml::', error)
            return null
        })
    return updateDocumentHtmlResponse
}

/**
 * Asynchronous function to update documentHtml by calling an API.
 *
 * @param {Object} requestData - The request data object.
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const bulkUpdateApiDocument = async (
    documentId,
    isReadyToCommit,
    isCommited,
    isDelete,
    isMerged,
    isRouted,
    userName,
    addressLabels = []
) => {
    // Use axios to send a PUT request to the specified endpoint
    let request = {
        documentId: documentId,
        isReadyToCommit: isReadyToCommit,
        isCommitted: isCommited,
        isDelete: isDelete,
        isMerged: isMerged,
        isRouted: isRouted,
        actionBy: userName,
        addressLabels: addressLabels,
    }
    const updateDocumentHtmlResponse = axios
        .put(
            Configurations.apiEndPoints.mergeDoc + 'documents/bulk/update',
            request
        )
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('bulk document update::', error)
            return null
        })
    return updateDocumentHtmlResponse
}

/**
 * Asynchronous function to delete document by calling an API.
 *
 * @param {Object} requestData - The request data object.
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const deleteApiDocument = async (documentId, userName) => {
    // Use axios to send a Delete request to the specified endpoint
    let request = {
        id: documentId,
        actionBy: userName,
    }

    console.log(`deleteApiDocument: ${JSON.stringify(request)}`)
    const deleteDocumentHtmlResponse = axios
        .delete(Configurations.apiEndPoints.mergeDoc + 'documents', request)
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('deleteDocument::', error)
            return null
        })
    return deleteDocumentHtmlResponse
}

/**
 * Asynchronous function to get a document by ID.
 *
 * @param {number} documentID - The ID of the document.
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const getDocumentById = async (data) => {
    const currenDocresponse = axios
        .post(
            Configurations.apiEndPoints.mergeDoc +
                'documents/getsingleormultiple',
            data
        )
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.log('getDocumentById::', error)
            return null
        })
    return currenDocresponse
}

/**
 * Asynchronous function to get a template content by ID.
 *
 * @param {number} templateID - the ID of the template.
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const getTemplateById = async (templateID) => {
    const templateResponse = axios
        .get(Configurations.apiEndPoints.mergeDoc + `templates/${templateID}`)
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.log('getTemplateById::', error)
            return null
        })
    return templateResponse
}

/**
 *  Asynchronous function to update the template content.
 *
 * @param {*} data
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const updateTemplate = async (data) => {
    const response = axios
        .put(Configurations.apiEndPoints.mergeDoc + `templates`, data)
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.log('updateTemplate::', error)
            return null
        })
    return response
}

/**
 *  Asynchronous function to update the spar content and controls.
 *
 * @param {*} data
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const updateSpar = async (data) => {
    const response = axios
        .put(Configurations.apiEndPoints.mergeDoc + `spars`, data)
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.log('updateSpar::', error)
            return null
        })
    return response
}

/**
 * Asynchronous function to fetch body parts data.
 *
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const fetchBodyParts = async () => {
    // Use axios to send a GET request to the specified endpoint
    const bodypartsResponse = axios
        .get(Configurations.apiEndPoints.mergeDoc + 'bodyparts')
        .then((response) => {
            // If the request is successful, return the response
            return response
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.log('fetchBodyParts::', error)
            return null
        })

    // const bodypartsResponse = mergeDocAPIService
    //   .get("assets/data/bodyParts.json")
    //   .then((response) => {
    //     // If the request is successful, return the response
    //     return response;
    //   })
    //   .catch((error) => {
    //     // If an error occurs during the request, log the error and return null
    //     console.log("fetchBodyParts::", error);
    //     return null;
    //   });
    return bodypartsResponse
}

/**
 * Asynchronous function to fetch body parts data.
 *
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const fetchCompHubBodyParts = async () => {
    // Use axios to send a GET request to the specified endpoint
    const bodypartsResponse = axios
        .get(Configurations.apiEndPoints.mergeDoc + 'bodyparts/new')
        .then((response) => {
            // If the request is successful, return the response
            return response
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.log('fetchCompHubBodyParts::', error)
            return null
        })

    return bodypartsResponse
}

/**
 * Asynchronous function to Applu Formulae in MOC-3 Template.
 *
 * @param {Object} request - The request object with claim number.
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const applyFormulae = async (request) => {
    //console.log('applyFormulae', JSON.stringify(request))
    const formulaeResponse = axios
        .post(
            Configurations.apiEndPoints.formulaeEngine +
                'api/mergedoc/applyformulae',
            request
        )
        .then((response) => {
            // If the request is successful, return the response
            //console.log("applyFormulae:: response.data", response);
            return response
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.log('applyFormulae::', error)
            return null
        })
    return formulaeResponse
}

/**
 * Asynchronous function to fetch LookUp Lists data.
 *
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const fetchLookUpLists = async (lookup) => {
    // Use axios to send a GET request to the specified endpoint
    const lookupResponse = axios
        .get(Configurations.apiEndPoints.mergeDoc + 'lookup/' + lookup)
        .then((response) => {
            // If the request is successful, return the response
            return response
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('fetchLookUpLists:', error)
            return null
        })
    return lookupResponse
}

/**
 * Asynchronous function to fetch Commissioner data.
 *
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const fetchcommissioners = async () => {
    // Use axios to send a GET request to the specified endpoint
    const CommissionersResponse = axios
        .get(Configurations.apiEndPoints.mergeDoc + 'commissioner')
        .then((response) => {
            // If the request is successful, return the response
            return response
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('fetchcommissioners:', error)
            return null
        })
    return CommissionersResponse
}

/**
 * Asynchronous function to fetch Commissioner Assistants data.
 *
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const fetchCommissionerAssistants = async () => {
    // Use axios to send a GET request to the specified endpoint
    const CommissionersResponse = axios
        .get(Configurations.apiEndPoints.mergeDoc + 'commissioner/assistant')
        .then((response) => {
            // If the request is successful, return the response
            return response
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('CommissionerAssistants:', error)
            return null
        })
    return CommissionersResponse
}

/**
 * Asynchronous function to fetch Court Reporter data.
 *
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const fetchCourtReporter = async () => {
    // Use axios to send a GET request to the specified endpoint
    const CommissionersResponse = axios
        .get(Configurations.apiEndPoints.mergeDoc + 'courtreporter')
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('CourtReporter:', error)
            return null
        })
    return CommissionersResponse
}

/**
 * Asynchronous function to fetch Choices Lists data.
 *
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const fetchChoices = async (name) => {
    // Use axios to send a GET request to the specified endpoint
    const choicesResponse = axios
        .get(Configurations.apiEndPoints.mergeDoc + 'choices/' + name)
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('fetchChoices:', error)
            return null
        })
    return choicesResponse
}

/**
 * Asynchronous function to fetch award types data.
 *
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const fetchAwardTypes = async () => {
    // Use axios to send a GET request to the specified endpoint
    const awardTypesResponse = axios
        .get(Configurations.apiEndPoints.mergeDoc + 'awardtype')
        .then((response) => {
            // If the request is successful, return the response
            return response
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.log('fetchAwardTypes::', error)
            return null
        })

    return awardTypesResponse
}

/**
 * Asynchronous function to fetch Commissioner data.
 *
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const adminFetchCommissioners = async () => {
    // Use axios to send a GET request to the specified endpoint
    const CommissionersResponse = axios
        .get(Configurations.apiEndPoints.admin + '/commissioner')
        .then((response) => {
            // If the request is successful, return the response
            return response
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('adminFetchCommissioners:', error)
            return null
        })
    return CommissionersResponse
}

/**
 * Asynchronous function to fetch Commissioner Assistants data.
 *
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const adminFetchCommissionerAssistants = async () => {
    // Use axios to send a GET request to the specified endpoint
    const AssistantsResponse = axios
        .get(Configurations.apiEndPoints.admin + 'commissioner/assistant')
        .then((response) => {
            // If the request is successful, return the response
            return response
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('adminFetchCommissionerAssistants:', error)
            return null
        })
    return AssistantsResponse
}

/**
 * Asynchronous function to add a new commissioner.
 *
 * @param {Object} commissionerData  - commissioner data to add.
 * @param {String} userName - email address of user adding the commissioner (account[0].username).
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const adminAddCommissioner = async (commissionerData, userName) => {
    // Use axios to send a POST request to the specified endpoint
    let request = {
        email: commissionerData.email,
        firstName: commissionerData.firstName,
        middleName: commissionerData.middleName,
        lastName: commissionerData.lastName,
        fullName: commissionerData.fullName,
        dob: commissionerData.dob,
        ssn: commissionerData.ssn,
        gender: commissionerData.gender,
        isActive: commissionerData.isActive,
        code: commissionerData.code,
        signature: commissionerData.signature,
        isRecall: commissionerData.isRecall,
        isChairPerson: commissionerData.isChairPerson,
        actionby: userName,
    }

    const addCommissionerResponse = axios
        .post(Configurations.apiEndPoints.admin + '/commissioner', request)
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('adminAddCommissioner:', error)
            return null
        })
    return addCommissionerResponse
}

/**
 * Asynchronous function to add a new commissioner assistant.
 *
 * @param {Object} commissionerAssistantData  - commissioner assistant data to add.
 * @param {String} userName - email address of user adding the commissioner (account[0].username).
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const adminAddCommissionerAssistant = async (
    commissionerAssistantData,
    userName
) => {
    // Use axios to send a POST request to the specified endpoint
    let request = {
        email: commissionerAssistantData.email,
        firstName: commissionerAssistantData.firstName,
        middleName: commissionerAssistantData.middleName,
        lastName: commissionerAssistantData.lastName,
        fullName: commissionerAssistantData.fullName,
        dob: commissionerAssistantData.dob,
        ssn: commissionerAssistantData.ssn,
        gender: commissionerAssistantData.gender,
        isActive: commissionerAssistantData.isActive,
        commissionerid: commissionerAssistantData.commissionerId,
        isSupervisor: commissionerAssistantData.isSupervisor,
        actionby: userName,
    }

    const addCommissionerAssistantResponse = axios
        .post(
            Configurations.apiEndPoints.admin + '/commissioner/assistant',
            request
        )
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('adminAddCommissionerAssistant:', error)
            return null
        })
    return addCommissionerAssistantResponse
}

/**
 * Asynchronous function to delete a commissioner.
 *
 * @param {String} commissionerEmail - Email of commissioner delete.
 * @param {String} userName - email address of user adding the commissioner (account[0].username).
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const adminDeleteCommissioner = async (commissionerEmail, userName) => {
    // Use axios to send a POST request to the specified endpoint
    let request = {
        email: commissionerEmail,
        actionby: userName,
    }

    const deleteCommissionerResponse = axios
        .delete(Configurations.apiEndPoints.admin + '/commissioner', {
            data: request,
        })
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('adminDeleteCommissioner:', error)
            return null
        })
    return deleteCommissionerResponse
}

/**
 * Asynchronous function to delete a commissioner assistant.
 *
 * @param {String} commissionerAssistantEmail - Email of commissioner assistantdelete.
 * @param {String} userName - email address of user adding the commissioner assistant (account[0].username).
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const adminDeleteCommissionerAssistant = async (
    commissionerAssistantEmail,
    userName
) => {
    // Use axios to send a POST request to the specified endpoint
    let request = {
        email: commissionerAssistantEmail,
        actionby: userName,
    }

    const deleteCommissionerAssistantResponse = axios
        .delete(Configurations.apiEndPoints.admin + '/commissioner/assistant', {
            data: request,
        })
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('adminDeleteCommissionerAssistant:', error)
            return null
        })
    return deleteCommissionerAssistantResponse
}

/**
 * Asynchronous function to update/edit an existing commissioner.
 *
 * @param {Object} commissionerData  - commissioner data to updat/edit.
 * @param {String} userName - email address of user adding the commissioner (account[0].username).
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const adminUpdateCommissioner = async (commissionerData, userName) => {
    // Use axios to send a POST request to the specified endpoint
    let request = {
        id: commissionerData.id,
        email: commissionerData.email,
        firstName: commissionerData.firstName,
        middleName: commissionerData.middleName,
        lastName: commissionerData.lastName,
        fullName: commissionerData.fullName,
        dob: commissionerData.dob,
        ssn: commissionerData.ssn,
        gender: commissionerData.gender,
        isActive: commissionerData.isActive,
        code: commissionerData.code,
        signature: commissionerData.signature,
        isRecall: commissionerData.isRecall,
        isChairPerson: commissionerData.isChairPerson,
        actionby: userName,
    }

    const updateCommissionerResponse = axios
        .put(Configurations.apiEndPoints.admin + '/commissioner', request)
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('adminUpdateCommissioner:', error)
            return null
        })
    return updateCommissionerResponse
}

/**
 * Asynchronous function to update/edit an existing commissioner assistant.
 *
 * @param {Object} commissionerData  - commissioner data to updat/edit.
 * @param {String} userName - email address of user adding the commissioner (account[0].username).
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const adminUpdateCommissionerAssistant = async (
    commissionerAssistantData,
    userName
) => {
    // Use axios to send a POST request to the specified endpoint
    let request = {
        id: commissionerAssistantData.id,
        email: commissionerAssistantData.email,
        firstName: commissionerAssistantData.firstName,
        middleName: commissionerAssistantData.middleName,
        lastName: commissionerAssistantData.lastName,
        fullName: commissionerAssistantData.fullName,
        dob: commissionerAssistantData.dob,
        ssn: commissionerAssistantData.ssn,
        gender: commissionerAssistantData.gender,
        isActive: commissionerAssistantData.isActive,
        commissionerId: commissionerAssistantData.commissionerId,
        isSupervisor: commissionerAssistantData.isSupervisor,
        actionby: userName,
    }

    const updateCommissionerAssistantResponse = axios
        .put(
            Configurations.apiEndPoints.admin + '/commissioner/assistant',
            request
        )
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('adminUpdateCommissionerAssistant:', error)
            return null
        })

    return updateCommissionerAssistantResponse
}

/**
 * Asynchronous function to fetch document data for a specific claim number.
 *
 * @param {String} claimNumber - The claim number.
 * @param {Number} numberOfDays - Retrieve documents available in the past within the specified number of days from now.
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const fetchDocumentByClaimNumber = async (claimNumber, numberOfDays) => {
    const documentResponse = axios
        .get(
            Configurations.apiEndPoints.mergeDoc +
                `documents/duplicate/claim/${claimNumber}/${numberOfDays}`
        )
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('fetchDocumentByClaimNumber:', error)
            return null
        })
    return documentResponse
}

/**
 * Asynchronous function to fetch the phrases for a given user
 *
 * @param {String} username - username/email
 * @returns the phrases for a given user
 */
export const fetchPhrasesByUserId = async (username) => {
    return axios
        .get(`${Configurations.apiEndPoints.common}userphrase/${username}`)
        .then((res) => {
            return res.data
        })
        .catch((err) => {
            console.error(err)
            return []
        })
}

/**
 * Asynchronous function to insert the phrases for a given user
 *
 * @param {Object} payload - username/email
 * @returns the response or error
 */
export const insertPhraseByUserId = async (payload) => {
    return axios
        .post(`${Configurations.apiEndPoints.common}userphrase`, payload)
        .then((res) => {
            return res.data
        })
        .catch((err) => {
            console.error(err)
            return []
        })
}

/**
 *
 * @param {*} payload
 * @returns the response or error
 */
export const deletePhraseByPhraseId = async (payload) => {
    return axios
        .delete(`${Configurations.apiEndPoints.common}userphrase`, {
            data: payload,
        })
        .then((res) => {
            return res.data
        })
        .catch((err) => {
            console.log(err)
            return []
        })
}

export const updatePhraseByPhraseId = async (payload) => {
    return axios
        .put(`${Configurations.apiEndPoints.common}userphrase`, payload)
        .then((res) => {
            return res.data
        })
        .catch((err) => {
            console.log(err)
            return []
        })
}

/**
 * Asynchronous function to commit the documents.
 *
 * @param {Object} request - The request object with order details and document.
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const commitToSharePoint = async (request) => {
    const labelResponse = axios
        .post(
            Configurations.apiEndPoints.formulaeEngine +
                'api/mergedoc/order/commit',
            request
        )
        .then((response) => {
            // If the request is successful, return the response
            return response
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('commitToSharePoint::', error)
            return null
        })
    return labelResponse
}

/**
 * Asynchronous function to route the order.
 *
 * @param {Object} request - The request object with order details for the routing.
 * @returns {Promise<*>} A promise that resolves to the API response or null in case of an error.
 */
export const routeOrder = async (request) => {
    const labelResponse = axios
        .post(
            Configurations.apiEndPoints.formulaeEngine +
                'api/mergedoc/order/route',
            request
        )
        .then((response) => {
            // If the request is successful, return the response
            return response
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('routeOrder::', error)
            return null
        })
    return labelResponse
}

/**
 * Function to commit the current document during merge.
 * The same document state later would be used to commit to sharepoint.
 * @param {*} request
 * @returns
 */
export const commitCurrentDocument = async (request) => {
    const labelResponse = axios
        .post(
            Configurations.apiEndPoints.formulaeEngine +
                'api/mergedoc/order/document',
            request
        )
        .then((response) => {
            // If the request is successful, return the response
            return response
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('commitCurrentDocument::', error)
            return null
        })
    return labelResponse
}

export const fetchDocumentFields = async (request) => {
    return axios
        .get(Configurations.apiEndPoints.mergeDoc + 'fields')
        .then((res) => {
            return res
        })
        .catch((error) => {
            console.error('fetchDocumentFields::', error)
            return null
        })
}

export const updateDocumentActionLogs = async (request) => {
    return axios
        .post(
            Configurations.apiEndPoints.mergeDoc + 'documentactionlogs',
            request
        )
        .then((res) => {
            return res
        })
        .catch((error) => {
            console.error('updateDocumentActionLogs::', error)
            return null
        })
}

export const fetchOrdersByStage = async (stage, status) => {
    const ordersResponse = axios
        .get(
            `${Configurations.apiEndPoints.baseUrl}api/mergedoc/documents/stage/${stage}/status/${status}`
        )
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('fetchOrdersByStage::', error)
            return null
        })
    return ordersResponse
}

export const fetchOrderDetailsByCriteria = async (name, criteria, user) => {
    const ordersResponse = axios
        .get(
            `${Configurations.apiEndPoints.baseUrl}api/mergedoc/documents/reports/${name}/details/${criteria}/${user}`
        )
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('fetchOrderDetailsByCriteria::', error)
            return null
        })
    return ordersResponse
}

export const reAssignmentOfOrders = async (request) => {
    return axios
        .put(
            Configurations.apiEndPoints.mergeDoc +
                'documents/assignment/update',
            request
        )
        .then((res) => {
            return res
        })
        .catch((error) => {
            console.error('reAssignmentOfOrders::', error)
            return null
        })
}

export const fetchSummaryKPIs = async () => {
    const summaryKPIsResponse = axios
        .get(`${Configurations.apiEndPoints.mergeDoc}documents/summary`)
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('fetchSummaryKPIs::', error)
            return null
        })
    return summaryKPIsResponse
}

export const fetchSummaryKPIsByUser = async () => {
    const summaryKPIsByUserResponse = axios
        .get(`${Configurations.apiEndPoints.mergeDoc}documents/summary/users`)
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('fetchSummaryKPIsByUser::', error)
            return null
        })
    return summaryKPIsByUserResponse
}

export const fetchDiffOfHearingAndCommited = async () => {
    const diffOfHearingAndCommitedResponse = axios
        .get(
            `${Configurations.apiEndPoints.mergeDoc}documents/hearing-committed-diff`
        )
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('fetchDiffOfHearingAndCommited::', error)
            return null
        })
    return diffOfHearingAndCommitedResponse
}

export const fetchRoutingSummary = async () => {
    const routingSummaryResponse = axios
        .get(`${Configurations.apiEndPoints.mergeDoc}documents/summary/routing`)
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('fetchRoutingSummary::', error)
            return null
        })
    return routingSummaryResponse
}

export const fetchBodyPartDifference = async () => {
    const claimvsOrderResponse = axios
        .get(`${Configurations.apiEndPoints.mergeDoc}bodyparts/claimvsorder`)
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('fetchBodyPartDifference::', error)
            return null
        })
    return claimvsOrderResponse
}

export const fetchUserProfileSettings = async (username) => {
    const profileSettingsResponse = axios
        .get(`${Configurations.apiEndPoints.common}usersettings/${username}`)
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('fetchUserProfileSettings::', error)
            return null
        })
    return profileSettingsResponse
}

export const updateUserProfileSettings = async (requestBody) => {
    const response = axios
        .put(`${Configurations.apiEndPoints.common}usersettings`, requestBody)
        .then((response) => {
            // If the request is successful, return the response
            return response.data
        })
        .catch((error) => {
            // If an error occurs during the request, log the error and return null
            console.error('updateUserProfileSettings::', error)
            return null
        })
    return response
}
